<script lang="ts">
        import YoutubeEmbed from '$lib/components/YoutubeEmbed.svelte';
        import {
                Carousel,
                CarouselContent,
                CarouselItem,
                CarouselNext,
                CarouselPrevious
        } from '$lib/components/ui/carousel';
        import { Button } from '$lib/components/ui/button';
        import type { CarouselAPI } from '$lib/components/ui/carousel/context';
        import { highlightVideos } from '$lib/data/highlight-videos';
        import { resolveAssetUrl } from '$lib/utils';
        import { onDestroy, onMount, tick } from 'svelte';

        type Clip = {
                title: string;
                videoUrl: string;
                posterUrl?: string;
                scrollToId?: string;
        };

        type ShowcaseCarousel = {
                title: string;
                description?: string;
                clips: Clip[];
        };

        const AUTO_SCROLL_TIMEOUTS_MS = [3200, 4200, 5200, 3800];

        const GRP1_DEMO_URL_1 = '/compressed_demo_videos/RBY1-Wrist-Elbow-Fast-Motion.mp4';
        const GRP1_DEMO_URL_2 = '/compressed_demo_videos/RBY1-Fine-Wrist-Movements.mp4';
        const GRP1_DEMO_URL_3 = '/compressed_demo_videos/RBY1-Torso-Control.mp4';

        const GRP2_DEMO_URL_1 = '/compressed_demo_videos/RBY1-Avoiding-Arm-Collision.mp4';
        const GRP2_DEMO_URL_2 = '/compressed_demo_videos/RBY1-IPmanSpin.m4v';
        const GRP2_DEMO_URL_3 = '/compressed_demo_videos/RBY1-Fine-Wrist-Movements-With-Filter.mp4';

        const GRP3_DEMO_URL_1 = '/compressed_demo_videos/RBY1-Xhand-Hanging-on-Another-Arm.mp4';
        const GRP3_DEMO_URL_2 = '/compressed_demo_videos/RBY1-Fast-Obj-Fetching.mp4';
        const GRP3_DEMO_URL_3 = '/compressed_demo_videos/RBY1-Xhand-Object-Hand-Over.mp4';

        const GRP4_DEMO_URL_1 = '/compressed_demo_videos/GR1-Policy-Coffee.mp4';
        const GRP4_DEMO_URL_2 = '/compressed_demo_videos/GR1-Policy-Pouring.mp4';

        const GRP5_DEMO_URL_1 = '/compressed_demo_videos/BiKino-ArmCross.mp4';
        const GRP5_DEMO_URL_2 = '/compressed_demo_videos/BiKino-ArmRolls.mp4';

        const DANCE_DEMO_URL = '/compressed_demo_videos/G1-Golden-Demo.mp4';

        const hiddenHighlightTitles = new Set(['Teleop Demo - Dual-Arm Reach', 'Fine Manipulation - SEW Constraints']);
        const visibleHighlightVideos = highlightVideos.filter((video) => !hiddenHighlightTitles.has(video.title));

        const showcaseCarousels: ShowcaseCarousel[] = [
                {
                        // title:  'We approach the pose retargeting problem using a first-principles viewpoint: \n utilizing math instead of learned models.',
                                                // title:  'We approach the pose retargeting problem using a first-principles viewpoint: \n utilizing math instead of learned models.',
                        title:  'We approach pose retargeting from a first-principles viewpoint, \n treating it as an orientation alignment problem.',

                        clips: [
                                { title: 'Clip 1/3: Wrist and Elbow Fast Motion', videoUrl: `${GRP1_DEMO_URL_1}#t=0,12` },
                                { title: 'Clip 2/3: Fine Wrist Movements', videoUrl: `${GRP1_DEMO_URL_2}#t=0,8` },
                                { title: 'Clip 3/3: Torso Control and Near Singularity Movements', videoUrl: `${GRP1_DEMO_URL_3}#t=0,14` }
                        ]
                },
                {
                        title: 'Iterative nonlinear optimization solvers are slow and may not converge, \n especially near singularities. \nInstead, we propose a constant-time, analytical approach \n that handles singularities with ease.',
                        clips: [
                                { title: 'Clip: Real-time human (right-half screen) to robot (left-half screen) pose retargeting using SEW algorithm.\n Singularity Margin shows how hard pose retargeting is for iterative methods.', videoUrl: `${DANCE_DEMO_URL}#t=0,30`, scrollToId: 'golden-demo' },
                        ]
                },
                {
                        title: 'Our analytical approach enables a fast safety filter that empowers instead of restricts. \nUsers can perform highly dynamic maneuvers without fear of self-collision.',
                        clips: [
                                { title: 'Clip 1/3: Filtering Aggressive Self-Collision', videoUrl: `${GRP2_DEMO_URL_1}#t=0,10` },
                                { title: 'Clip 2/3: Continuous Near Self-Collision Movement', videoUrl: `${GRP2_DEMO_URL_2}#t=0,15` },
                                { title: 'Clip 3/3: Near Self-Collision Fine Movements', videoUrl: `${GRP2_DEMO_URL_3}#t=0,10` }
                        ]
                },
                {
                        title: 'The SEW action representation can be used to train fine dexterous manipulation. \n We achieved a similar success rate on tasks introduced \n in <a href="https://dexmimicgen.github.io/" target="_blank" rel="noreferrer" class="underline hover:text-primary transition-colors">DexMimicGen</a> using their trajectories.',
                        clips: [
                                { title: 'Clip 1/2: Coffee Making Task with GR1', videoUrl: `${GRP4_DEMO_URL_1}#t=0,9` },
                                { title: 'Clip 2/2: Object Pouring Task with GR1', videoUrl: `${GRP4_DEMO_URL_2}#t=0,10` }
                        ]
                },
                {
                        title: 'Our closed-form, CPU-only solution conserves computational resources \n that can be used for other, unstructured problems.',
                        clips: [
                                { title: 'Clip 1/3: Making the Entire Arm Useful', videoUrl: `${GRP3_DEMO_URL_1}#t=0,15` },
                                { title: 'Clip 2/3: Towards More Dynamic Demonstrations', videoUrl: `${GRP3_DEMO_URL_2}#t=0,10` },
                                { title: 'Clip 3/3: Capable of Bimanual Coordination', videoUrl: `${GRP3_DEMO_URL_3}#t=0,15` }
                        ]
                },
                {
                        title: 'Our method easily generalizes to other robot platforms, including bimanual manipulators \n with twice the arm span and different wrist structure.',
                        clips: [
                                { title: 'Clip 1/2: Dual-Kinova3-14-DoF Robot Crossing Arms', videoUrl: `${GRP5_DEMO_URL_1}#t=0,6` },
                                { title: 'Clip 2/2: Self-Collision Filter Enabled for the Arm Rolling Motion', videoUrl: `${GRP5_DEMO_URL_2}#t=0,7` }
                        ]
                }
        ];

        let carouselApis: Array<CarouselAPI | undefined> = showcaseCarousels.map(() => undefined);
        let carouselVideos: Array<Array<HTMLVideoElement | null>> = showcaseCarousels.map((carousel) =>
                carousel.clips.map(() => null)
        );
        const carouselTimers: Array<ReturnType<typeof setTimeout> | undefined> = showcaseCarousels.map(
                () => undefined
        );
        const carouselCleanup: Array<(() => void) | undefined> = showcaseCarousels.map(() => undefined);

        let carouselElements: Array<HTMLElement | null> = showcaseCarousels.map(() => null);
        let carouselVisibility: boolean[] = showcaseCarousels.map(() => false);

        const clearCarouselTimer = (carouselIndex: number) => {
                const timer = carouselTimers[carouselIndex];
                if (timer) {
                        clearTimeout(timer);
                        carouselTimers[carouselIndex] = undefined;
                }
        };

        const playSelectedVideo = (carouselIndex: number) => {
                const api = carouselApis[carouselIndex];
                if (!api) return;

                const selectedIndex = api.selectedScrollSnap();
                const videos = carouselVideos[carouselIndex] ?? [];
                // const clips = showcaseCarousels[carouselIndex].clips;

                videos.forEach((video, index) => {
                        if (!video) return;
                        if (index === selectedIndex) {
                                video.currentTime = 0;
                                void video.play().catch(() => {});
                                return;
                        }

                        video.pause();
                        video.currentTime = 0;
                });
        };

        const scheduleAutoScroll = (carouselIndex: number) => {
                clearCarouselTimer(carouselIndex);

                const api = carouselApis[carouselIndex];
                if (!api) return;

                const selectedIndex = api.selectedScrollSnap();
                const clips = showcaseCarousels[carouselIndex].clips;
                const currentClip = clips[selectedIndex % clips.length];

                // Parse duration from video URL hash (e.g., #t=0,4)
                let delayMs = 5000; // Default fallback
                const timeMatch = currentClip.videoUrl.match(/#t=(\d+(?:\.\d+)?),(\d+(?:\.\d+)?)/);
                if (timeMatch) {
                        const [, start, end] = timeMatch;
                        delayMs = (parseFloat(end) - parseFloat(start)) * 1000;
                }

                // Add a small buffer to ensure video finishes
                delayMs += 500;

                carouselTimers[carouselIndex] = setTimeout(() => {
                        if (clips.length === 1) {
                                const video = carouselVideos[carouselIndex][0];
                                if (video) {
                                        video.currentTime = 0;
                                        void video.play().catch(() => {});
                                        scheduleAutoScroll(carouselIndex);
                                }
                        } else {
                                carouselApis[carouselIndex]?.scrollNext();
                        }
                }, delayMs);
        };

        const handleCarouselSelect = (carouselIndex: number) => {
                if (!carouselVisibility[carouselIndex]) return;
                playSelectedVideo(carouselIndex);
                scheduleAutoScroll(carouselIndex);
        };

        const setShowcaseCarouselApi = (carouselIndex: number) => {
                return (api: CarouselAPI | undefined) => {
                        carouselCleanup[carouselIndex]?.();
                        carouselCleanup[carouselIndex] = undefined;

                        carouselApis[carouselIndex] = api;
                        if (!api) return;

                        const onSelect = () => handleCarouselSelect(carouselIndex);
                        api.on('select', onSelect);
                        api.on('reInit', onSelect);

                        carouselCleanup[carouselIndex] = () => {
                                clearCarouselTimer(carouselIndex);
                                api.off('select', onSelect);
                                api.off('reInit', onSelect);
                        };

                        void tick().then(onSelect);
                };
        };

        const handleClipEnded = (carouselIndex: number) => {
                const api = carouselApis[carouselIndex];
                if (!api) return;

                // Clear any pending auto-scroll timer to prevent double-skipping
                clearCarouselTimer(carouselIndex);

                const clips = showcaseCarousels[carouselIndex].clips;
                if (clips.length === 1) {
                        const video = carouselVideos[carouselIndex][0];
                        if (video) {
                                video.currentTime = 0;
                                void video.play().catch(() => {});
                                scheduleAutoScroll(carouselIndex);
                        }
                } else {
                        api.scrollNext();
                }
        };

        onMount(() => {
                const observer = new IntersectionObserver((entries) => {
                        entries.forEach(entry => {
                                const index = carouselElements.indexOf(entry.target as HTMLElement);
                                if (index !== -1) {
                                        const wasVisible = carouselVisibility[index];
                                        const isVisible = entry.isIntersecting;
                                        carouselVisibility[index] = isVisible;

                                        if (isVisible && !wasVisible) {
                                                // Became visible: play
                                                playSelectedVideo(index);
                                                scheduleAutoScroll(index);
                                        } else if (!isVisible && wasVisible) {
                                                // Became hidden: pause and clear timer
                                                const videos = carouselVideos[index] ?? [];
                                                videos.forEach(v => v?.pause());
                                                clearCarouselTimer(index);
                                        }
                                }
                        });
                }, { threshold: 0.2 });

                carouselElements.forEach(el => {
                        if (el) observer.observe(el);
                });

                return () => observer.disconnect();
        });

        onDestroy(() => {
                carouselCleanup.forEach((cleanup) => cleanup?.());
        });
</script>

<svelte:head>
        <title>SEW Geometric Teleop | Videos</title>
</svelte:head>

<main class="grid grid-cols-[1fr_min(80ch,100%)_1fr] gap-y-10 py-12">
        <div class="col-start-2 px-4 text-center">
		<h1 class="text-5xl font-bold tracking-tighter">
			We propose <code class="rounded bg-muted/40 px-1 py-0.5 font-mono text-[0.9em]">SEW-MIMIC</code>:
      <br/>
			<span class="font-normal text-muted-foreground">an analytical method for exact human-robot bimanual pose retargeting</span>
		</h1>
                <!-- <p class="mx-auto mt-4 max-w-2xl text-muted-foreground md:text-xl/relaxed lg:text-base/relaxed xl:text-xl/relaxed">
                        a method for exact human-robot bimanual pose retargeting.
                </p> -->
        </div>

        <section class="col-span-3 py-16">
                <div class="px-4">
                        <div class="mx-auto max-w-6xl space-y-12">
                                <div class="space-y-12">
                                        {#each showcaseCarousels as carousel, carouselIndex (carouselIndex)}
                                                <article class="space-y-4">
                                                        <header class="space-y-1 text-center">
								<h3 class="text-xl font-semibold whitespace-pre-line">{@html carousel.title}</h3>
                                                                <p class="mx-auto max-w-3xl text-sm text-muted-foreground md:text-base/relaxed">
                                                                        {@html carousel.description ?? ''}
                                                                </p>
                                                        </header>

                                                        <div class="mx-auto max-w-3xl" bind:this={carouselElements[carouselIndex]}>
                                                                <Carousel
                                                                        class="relative"
                                                                        opts={{ loop: true, align: 'start' }}
                                                                        setApi={setShowcaseCarouselApi(carouselIndex)}
                                                                >
                                                                        <CarouselContent class="py-1">
                                                                                {#each carousel.clips as clip, clipIndex (clip.videoUrl)}
                                                                                        <CarouselItem>
                                                                                                <div class="space-y-3">
                                                                                                        <div
                                                                                                                class="aspect-video overflow-hidden rounded-xl border border-border/50 bg-muted/20 shadow-lg"
                                                                                                        >
                                                                                                                <!-- svelte-ignore a11y-media-has-caption -->
                                                                                                                <video
                                                                                                                        bind:this={carouselVideos[carouselIndex][clipIndex]}
                                                                                                                        class="h-full w-full object-cover"
                                                                                                                        muted
                                                                                                                        playsinline
                                                                                                                        preload="none"
                                                                                                                        on:ended={() => handleClipEnded(carouselIndex)}
                                                                                                                >
                                                                                                                        <source
                                                                                                                                src={resolveAssetUrl(clip.videoUrl)}
                                                                                                                                type="video/mp4"
                                                                                                                        />
                                                                                                                        Your browser does not support embedded videos.
                                                                                                                </video>
                                                                                                        </div>
                                                                                                        <p class="text-sm text-muted-foreground whitespace-pre-line">
                                                                                                                {clip.title}
                                                                                                        </p>
                                                                                                        {#if clip.scrollToId}
                                                                                                                <Button
                                                                                                                        variant="outline"
                                                                                                                        size="sm"
                                                                                                                        class="w-full"
                                                                                                                        onclick={() => {
                                                                                                                                if (clip.scrollToId) {
                                                                                                                                        document.getElementById(clip.scrollToId)?.scrollIntoView({ behavior: 'smooth' });
                                                                                                                                }
                                                                                                                        }}
                                                                                                                >
                                                                                                                        Scroll to the Full Video
                                                                                                                </Button>
                                                                                                        {/if}
                                                                                                </div>
                                                                                        </CarouselItem>
                                                                                {/each}
                                                                        </CarouselContent>

                                                                        <CarouselPrevious
                                                                                class="border-border/60 bg-background/70 text-foreground backdrop-blur hover:bg-background/90"
                                                                        />
                                                                        <CarouselNext
                                                                                class="border-border/60 bg-background/70 text-foreground backdrop-blur hover:bg-background/90"
                                                                        />
                                                                </Carousel>
                                                        </div>
                                                </article>
                                        {/each}
                                </div>
                        </div>
                </div>
        </section>

        <section class="col-span-3 bg-neutral-900 py-16 text-white">
                <div class="col-start-2 px-4">
                        <div class="mx-auto max-w-4xl space-y-10">
                                {#each visibleHighlightVideos as video (video.title)}
                                        <article class="space-y-4" id={video.title.includes('whole-body') ? 'golden-demo' : undefined}>
                                                <header class="space-y-1 text-left">
							<h2 class="text-2xl font-semibold text-white whitespace-pre-line">{video.title}</h2>
                                                        <p class="text-sm text-neutral-300 md:text-base/relaxed">{video.description}</p>
                                                        {#if video.videoUrl || video.youtubeId}
                                                                <a
                                                                        class="text-xs uppercase tracking-wide text-neutral-400 underline decoration-dotted underline-offset-4 transition-colors hover:text-neutral-200"
                                                                        href={video.videoUrl
                                                                                ? resolveAssetUrl(video.videoUrl)
                                                                                : `https://youtu.be/${video.youtubeId}`}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                >
                                                                        <!-- {video.youtubeId ? 'Watch on YouTube' : 'Open source video'} -->
                                                                </a>
                                                        {/if}
                                                </header>
                                                {#if video.videoUrl}
                                                        <div class="aspect-video overflow-hidden rounded-xl border border-white/10 bg-black/40 shadow-lg">
                                                                <!-- svelte-ignore a11y-media-has-caption -->
                                                                <video
                                                                        title={`Playback for ${video.title}`}
                                                                        class="h-full w-full object-cover"
                                                                        controls
                                                                        playsinline
                                                                        poster={video.posterUrl ? resolveAssetUrl(video.posterUrl) : undefined}
                                                                >
                                                                        <source src={resolveAssetUrl(video.videoUrl)} type="video/mp4" />
                                                                        Your browser does not support embedded videos.
                                                                </video>
                                                        </div>
                                                {:else if video.youtubeId}
                                                        <YoutubeEmbed videoId={video.youtubeId} title={`Playback for ${video.title}`} />
                                                {:else}
                                                        <p class="text-sm text-neutral-300">Video source unavailable.</p>
                                                {/if}
                                        </article>
                                {/each}
                        </div>
                </div>
        </section>
</main>
